/**
 * Organization role helper.
 *
 * An "org admin" (for visibility purposes) is the organization OWNER or one of its
 * additional owners. This matches the existing `hasManagementAccess()` checks used
 * elsewhere in the app (e.g. QuizList).
 *
 * Org admins get an org-wide view of reports / projects / action items; regular members
 * see only what they own, are assigned to, or are a member of.
 *
 * Note: `activeOrganization.owner` holds the owner's Cognito sub, while
 * `additionalOwners` holds emails — so we compare sub-to-owner and email-to-additionalOwners.
 *
 * @param {object|null} activeOrganization - The active organization object.
 * @param {string|null} userSub - Current user's Cognito sub.
 * @param {string|null} userEmail - Current user's email.
 * @returns {boolean}
 */
export const isOrgAdmin = (activeOrganization, userSub, userEmail) => {
  if (!activeOrganization) return false;
  if (userSub && activeOrganization.owner === userSub) return true;
  const additionalOwners = Array.isArray(activeOrganization.additionalOwners)
    ? activeOrganization.additionalOwners
    : [];
  if (userEmail && additionalOwners.includes(userEmail)) return true;
  return false;
};

/**
 * Whether the current user may use the AI chat in this organization.
 *
 * Access = org owner / co-owner, OR a member who has NOT been disabled by an
 * admin. "Disabled" is the org's `aiDisabledUsers` denylist (a userSub array) —
 * the exact same source the admin UI uses to grant/revoke chat per member
 * (OrganizationManagement computes `!aiDisabledUsers.includes(userSub)`).
 *
 * NOTE: we intentionally do NOT require looking up an ACTIVE OrganizationMember
 * record here. That lookup (a filtered listOrganizationMembers scan + strict
 * `status === 'ACTIVE'` check) used to gate access and would wrongly block
 * enabled members when the scan paged past their row or their status wasn't
 * exactly 'ACTIVE'. If the user has this org active they already belong to it,
 * so the denylist alone is the source of truth.
 *
 * @param {object|null} activeOrganization
 * @param {string|null} userSub
 * @param {string|null} userEmail
 * @returns {boolean}
 */
export const hasChatAccess = (activeOrganization, userSub, userEmail) => {
  if (!activeOrganization || !userSub) return false;
  if (isOrgAdmin(activeOrganization, userSub, userEmail)) return true;
  const aiDisabledUsers = Array.isArray(activeOrganization.aiDisabledUsers)
    ? activeOrganization.aiDisabledUsers
    : [];
  return !aiDisabledUsers.includes(userSub);
};

export default isOrgAdmin;
